import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button, Modal, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Copy, Send, FilePlus, Plus, Calendar, Merge, FileCheck, Save, Trash2, Settings, RefreshCw, BarChart3, CheckCircle, Repeat, CheckSquare, Flag, BookOpen, ListChecks } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import TestStructureService, {
  type LookupItem,
  type TestStructureResponse,
  type TestStructureCell,
  type TestStructureCategory,
} from "../../_services/TestStructureService";
import QuickQuizService from "../../_services/QuickQuizService";
import ConfigEditorModal from "./ConfigEditorModal";
import ElementGuard from "../../access-control/ElementGuard";
import AccessControlEditorModal from "../../access-control/AccessControlEditorModal";
import "./test-structure.styles.css";

const PAGE_KEY = "test-structure";

// ==================== Helpers ====================

function formatDate(d: string | null): string {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function coverageTone(attempted: number, denominator: number): "full" | "good" | "warn" | "low" | "none" {
  if (!denominator) return "none";
  const pct = attempted / denominator;
  if (pct >= 1) return "full";
  if (pct >= 0.75) return "good";
  if (pct >= 0.4) return "warn";
  return "low";
}

// Placeholder "coming soon" toolbar action
function SoonButton({
  label,
  icon,
  onClick,
  variant = "light",
}: {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  variant?: string;
}) {
  const handler =
    onClick ||
    (() => {
      // eslint-disable-next-line no-alert
      alert(`${label} — coming soon`);
    });
  const isIconOnly = !!icon;
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{label}</Tooltip>}>
      <button
        style={isIconOnly ? undefined : { backgroundColor: variant === "success" ? "#059669" : "#6b7280", color: "var(--text-on-brand)", border: "none" }}
        className={isIconOnly ? "btn btn-sm ts-icon-btn" : "ts-action-btn"}
        onClick={handler}
      >
        {icon || label}
      </button>
    </OverlayTrigger>
  );
}

// ==================== Page ====================

export default function TestStructurePage() {
  const history = useHistory();
  const location = useLocation();

  const initialParams = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      courseTypeId: sp.get("courseTypeId") ? Number(sp.get("courseTypeId")) : null,
      programId: sp.get("programId") ? Number(sp.get("programId")) : null,
      yearId: sp.get("yearId") ? Number(sp.get("yearId")) : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [courseTypes, setCourseTypes] = useState<LookupItem[]>([]);
  const [programs, setPrograms] = useState<LookupItem[]>([]);
  const [years, setYears] = useState<LookupItem[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [yearsLoading, setYearsLoading] = useState(false);

  const [courseTypeId, setCourseTypeId] = useState<number | null>(initialParams.courseTypeId);
  const [programId, setProgramId] = useState<number | null>(initialParams.programId);
  const [yearId, setYearId] = useState<number | null>(initialParams.yearId);

  const [data, setData] = useState<TestStructureResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create Standard Tests — in-flight termId or "program" for program-level
  const [standardTestsBusy, setStandardTestsBusy] = useState<number | "program" | null>(null);
  const [standardTestsMsg, setStandardTestsMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Add existing test modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingAdd, setPendingAdd] = useState<{
    testId: number;
    testName: string;
    courses: Array<{ id: number; name: string }>;
    selectedCourseId: number | null;
  } | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addMsg, setAddMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Remove confirm modal
  const [removeTarget, setRemoveTarget] = useState<TestStructureCell | null>(null);
  const [removing, setRemoving] = useState(false);

  // Program Level Publish
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishStatus, setPublishStatus] = useState<{ publishToStaff: boolean; publishToStudent: boolean } | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);

  // Test Timetable (schedule tests per term+category)
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [timetableData, setTimetableData] = useState<any>(null);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [timetableName, setTimetableName] = useState("");
  const [timetableTests, setTimetableTests] = useState<Array<{ testId: number; name: string; date: string; courseName: string; startTime: string; endTime: string; remarks: string }>>([]);
  const [timetableBatches, setTimetableBatches] = useState<Array<{ id: number; name: string; checked: boolean }>>([]);
  const [timetableRemarks, setTimetableRemarks] = useState("");
  const [timetableMode, setTimetableMode] = useState<"view" | "create" | "edit">("view");
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [timetableSaving, setTimetableSaving] = useState(false);
  const [timetableMsg, setTimetableMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // DVS Pattern modal
  const [showDvsModal, setShowDvsModal] = useState(false);
  const [dvsTermCat, setDvsTermCat] = useState<{ termId: number; termName: string; categoryId: number; categoryName: string } | null>(null);
  const [dvsPatterns, setDvsPatterns] = useState<Array<{ id: number; name: string }>>([]);
  const [dvsSelected, setDvsSelected] = useState<string>("-1");
  const [dvsBusy, setDvsBusy] = useState(false);
  const [dvsMsg, setDvsMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Data entry status map for freeze button visibility (termId_categoryId → status text)
  const [dataEntryStatus, setDataEntryStatus] = useState<Record<string, string>>({});

  // Open Entry modal
  const [showOpenEntryModal, setShowOpenEntryModal] = useState(false);
  const [openEntryTarget, setOpenEntryTarget] = useState<{ termId: number; termName: string; categoryId: number; categoryName: string } | null>(null);
  const [openEntryBusy, setOpenEntryBusy] = useState(false);
  const [openEntryMsg, setOpenEntryMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Access Control admin modal
  const [showAccessControlModal, setShowAccessControlModal] = useState(false);

  // Category Config (Settings) modal
  const [showCatConfigModal, setShowCatConfigModal] = useState(false);
  const [catConfigTarget, setCatConfigTarget] = useState<{ termId: number; termName: string; categoryId: number; categoryName: string } | null>(null);
  const [catConfigLoading, setCatConfigLoading] = useState(false);
  const [catConfigMergeLogic, setCatConfigMergeLogic] = useState("1");
  const [catConfigMappings, setCatConfigMappings] = useState<Array<{ catId: string; courseType2Id: string; value: string }>>([]);
  const [catConfigCategories, setCatConfigCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [catConfigCourseTypes2, setCatConfigCourseTypes2] = useState<Array<{ id: number; name: string }>>([]);
  const [catConfigSaving, setCatConfigSaving] = useState(false);
  const [catConfigMsg, setCatConfigMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Pass Marks modal
  const [showPassMarksModal, setShowPassMarksModal] = useState(false);
  const [passMarksLoading, setPassMarksLoading] = useState(false);
  const [passMarksTests, setPassMarksTests] = useState<Array<{ testId: number; testName: string; testDate: string; sections: Array<{ sectionId: number; sectionName: string; maxMarks: number; threshold: number }> }>>([]);
  const [passMarksSaving, setPassMarksSaving] = useState(false);
  const [passMarksMsg, setPassMarksMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [passMarksChecked, setPassMarksChecked] = useState<Set<number>>(new Set());

  // Reallocate / Month-Year confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ type: "reallocate" | "monthYear"; termId: number; categoryId: number } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Merge state
  const [mergeBusyKey, setMergeBusyKey] = useState<string | null>(null); // "termId_catId_courseId" or "all_termId_catId"
  const [mergeAllMsg, setMergeAllMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Compute Grade modal
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [gradeTarget, setGradeTarget] = useState<{ termId: number; categoryId: number } | null>(null);
  const [gradeTypeId, setGradeTypeId] = useState(1); // 1=Absolute, 2=Relative
  const [gradeBusy, setGradeBusy] = useState(false);
  const [gradeMsg, setGradeMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [gradeBoundaries, setGradeBoundaries] = useState<Array<{ testName: string; gradeBoundaryJson: Array<{ grade: string; lowerBound: number; upperBound: number }> }>>([]);
  const [gradeBoundariesLoading, setGradeBoundariesLoading] = useState(false);
  const [eraseGradeBoundaries, setEraseGradeBoundaries] = useState(false);

  // SGPA/CGPA modal
  const [showSgpaCgpaModal, setShowSgpaCgpaModal] = useState(false);
  const [sgpaCgpaTarget, setSgpaCgpaTarget] = useState<{ termId: number; categoryId: number } | null>(null);
  const [sgpaCgpaBusyMode, setSgpaCgpaBusyMode] = useState<"sgpa" | "cgpa" | null>(null);
  const [sgpaCgpaMsg, setSgpaCgpaMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Publish Report modal
  const [showPublishReportModal, setShowPublishReportModal] = useState(false);
  const [publishReportTarget, setPublishReportTarget] = useState<{ termId: number; categoryId: number } | null>(null);
  const [publishReportStatus, setPublishReportStatus] = useState<{ publishToStaff: boolean; publishToStudent: boolean } | null>(null);
  const [publishReportBusy, setPublishReportBusy] = useState(false);

  // Exam Dates modal
  const [showExamDatesModal, setShowExamDatesModal] = useState(false);
  const [examDatesLoading, setExamDatesLoading] = useState(false);
  const [examDatesBatches, setExamDatesBatches] = useState<Array<{ id: number; name: string }>>([]);
  const [examDatesIndexes, setExamDatesIndexes] = useState<Array<{ id: number; name: string }>>([]);
  const [examDatesRows, setExamDatesRows] = useState<Array<{ mappingId: number; batchId: number; startDate: string; endDate: string; indexId: number }>>([]);
  const [examDatesTermId, setExamDatesTermId] = useState(0);
  const [examDatesSaving, setExamDatesSaving] = useState(false);
  const [examDatesMsg, setExamDatesMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Program Config Editor modal
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Borrow Test Rules — clone Config from another year
  const [showBorrowModal, setShowBorrowModal] = useState(false);
  const [borrowYears, setBorrowYears] = useState<Array<{ yearId: number; yearName: string }>>([]);
  const [selectedCloneYearId, setSelectedCloneYearId] = useState<number | null>(null);
  const [borrowBusy, setBorrowBusy] = useState(false);
  const [borrowMsg, setBorrowMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // ------------------- URL sync -------------------
  useEffect(() => {
    const sp = new URLSearchParams();
    if (courseTypeId) sp.set("courseTypeId", String(courseTypeId));
    if (programId) sp.set("programId", String(programId));
    if (yearId) sp.set("yearId", String(yearId));
    const search = sp.toString() ? `?${sp.toString()}` : "";
    if (location.search !== search) {
      history.replace({ pathname: location.pathname, search });
    }
  }, [courseTypeId, programId, yearId, history, location.pathname, location.search]);

  // ------------------- Cascading lookups (mirror Java AJAX flow) -------------------
  // Load course types once on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await TestStructureService.getCourseTypes();
        setCourseTypes(data);
      } catch (e) {
        console.error("Failed to load course types", e);
      }
    })();
  }, []);

  // Load programs whenever course type changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProgramsLoading(true);
      try {
        const data = await TestStructureService.getPrograms(courseTypeId);
        if (cancelled) return;
        setPrograms(data);
        // Drop current selection if it's no longer in the list
        if (programId && !data.find((p) => p.id === programId)) {
          setProgramId(null);
          setYears([]);
          setYearId(null);
        }
      } catch (e) {
        console.error("Failed to load programs", e);
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseTypeId]);

  // Load years whenever program changes
  useEffect(() => {
    let cancelled = false;
    if (!programId) {
      setYears([]);
      return;
    }
    (async () => {
      setYearsLoading(true);
      try {
        const data = await TestStructureService.getYears(programId);
        if (cancelled) return;
        setYears(data);
        if (yearId && !data.find((y) => y.id === yearId)) {
          setYearId(null);
        }
      } catch (e) {
        console.error("Failed to load years", e);
      } finally {
        if (!cancelled) setYearsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  // ------------------- Load structure -------------------
  const loadStructure = useCallback(async () => {
    if (!programId || !yearId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [resp, statusMap] = await Promise.all([
        TestStructureService.getStructure(programId, yearId),
        TestStructureService.getDataEntryStatus(programId, yearId),
      ]);
      setData(resp);
      setDataEntryStatus(statusMap);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load test structure");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [programId, yearId]);

  useEffect(() => {
    loadStructure();
  }, [loadStructure]);

  // ------------------- Add test search -------------------
  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    setAddMsg(null);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await QuickQuizService.listQuizzes({ q: q.trim(), limit: 20 });
        setSearchResults(res.quizzes || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const handlePickTest = async (test: any) => {
    if (!programId) return;
    setPendingAdd({
      testId: test.testId,
      testName: test.name,
      courses: [],
      selectedCourseId: null,
    });
    try {
      const resp: any = await QuickQuizService.getPublishLookups(test.testId);
      setPendingAdd((prev) =>
        prev ? { ...prev, courses: resp?.courses || [] } : null,
      );
    } catch {
      /* ignore */
    }
  };

  const handleConfirmAdd = async () => {
    if (!pendingAdd || !pendingAdd.selectedCourseId || !programId) return;
    setAddBusy(true);
    try {
      await QuickQuizService.issueToCourse(pendingAdd.testId, {
        courseId: pendingAdd.selectedCourseId,
        programId,
      });
      setPendingAdd(null);
      setShowAddModal(false);
      setAddMsg({ type: "success", text: "Test added to program" });
      loadStructure();
    } catch (e: any) {
      setAddMsg({
        type: "error",
        text: e?.response?.data?.message || "Failed to add test",
      });
    } finally {
      setAddBusy(false);
    }
  };

  // ------------------- Remove test mapping -------------------
  const handleRemove = async () => {
    if (!removeTarget || !programId) return;
    setRemoving(true);
    try {
      await QuickQuizService.removeProgramTest(removeTarget.testId, programId);
      setRemoveTarget(null);
      loadStructure();
    } catch (e) {
      console.error("Remove failed", e);
    } finally {
      setRemoving(false);
    }
  };

  // ------------------- Create test for a cell -------------------
  // ── DVS Pattern handlers ──
  const openDvsModal = async (termId: number, termName: string, catId: number, catName: string) => {
    if (!programId || !yearId) return;
    setDvsTermCat({ termId, termName, categoryId: catId, categoryName: catName });
    setShowDvsModal(true);
    setDvsMsg(null);
    setDvsBusy(true);
    try {
      const data = await TestStructureService.getDvsPatterns(programId, yearId, termId, catId);
      setDvsPatterns(data.patterns || []);
      setDvsSelected(data.selectedPatternIds?.length > 0 ? data.selectedPatternIds[0] : "-1");
    } catch { setDvsPatterns([]); setDvsSelected("-1"); }
    setDvsBusy(false);
  };

  const handleDvsSave = async () => {
    if (!programId || !yearId || !dvsTermCat) return;
    setDvsBusy(true);
    setDvsMsg(null);
    try {
      const ids = dvsSelected !== "-1" ? [dvsSelected] : [];
      const resp = await TestStructureService.saveDvsPattern(programId, yearId, dvsTermCat.termId, dvsTermCat.categoryId, ids);
      setDvsMsg({ type: resp.success ? "success" : "error", text: resp.message });
    } catch (e: any) {
      setDvsMsg({ type: "error", text: e?.response?.data?.message || "Failed to save." });
    }
    setDvsBusy(false);
  };

  // ── Test Timetable handlers ──
  const openTimetable = async (termId: number, catId: number) => {
    if (!programId || !yearId) return;
    setShowTimetableModal(true);
    setTimetableLoading(true);
    setTimetableData(null);
    setTimetableMsg(null);
    setTimetableName("");
    setTimetableRemarks("");
    setTimetableMode("view");
    try {
      const data = await TestStructureService.getTestTimetable(programId, termId, catId, yearId);
      setTimetableData(data);
      setTimetableTests(
        (data.tests || []).map((t: any) => ({
          testId: t.id, name: t.name, date: t.date || "", courseName: t.courseName || "",
          startTime: "05:00", endTime: "05:15", remarks: "Syllabus",
        }))
      );
      setTimetableBatches(
        (data.batches || []).map((b: any) => ({ id: b.id, name: b.name, checked: false }))
      );
      setTimetableName(`${data.programName}_${data.termName}_${data.categoryName}`);
    } catch (e: any) {
      const detail = e?.response?.data?.error || e?.response?.data?.message || e?.message || "Unknown error";
      setTimetableMsg({ type: "error", text: `Failed to load timetable data: ${detail}` });
    }
    setTimetableLoading(false);
  };

  const openTimetableEdit = (tmpl: any) => {
    setEditingTemplateId(tmpl.id);
    setTimetableName(tmpl.name || "");
    setTimetableRemarks(tmpl.remarks || "");
    setTimetableTests(
      (tmpl.slots || []).map((s: any) => ({
        testId: s.testId, name: s.testName || "", date: s.testDate || "",
        courseName: s.courseName || "",
        startTime: s.startTime || "", endTime: s.endTime || "", remarks: s.remarks || "",
      }))
    );
    // Pre-check batches that are already mapped to this template
    const mappedBatchIds = new Set((tmpl.batches || []).map((b: any) => b.id));
    setTimetableBatches((prev) => prev.map((b) => ({ ...b, checked: mappedBatchIds.has(b.id) })));
    setTimetableMode("edit");
  };

  const handleTimetableFieldChange = (idx: number, field: string, value: string) => {
    setTimetableTests((prev) => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const handleBatchToggle = (idx: number) => {
    setTimetableBatches((prev) => prev.map((b, i) => i === idx ? { ...b, checked: !b.checked } : b));
  };

  const handleBatchToggleAll = () => {
    const allChecked = timetableBatches.every((b) => b.checked);
    setTimetableBatches((prev) => prev.map((b) => ({ ...b, checked: !allChecked })));
  };

  const handleTimetableSave = async () => {
    if (!timetableData || !timetableName) return;
    setTimetableSaving(true);
    setTimetableMsg(null);
    const testsPayload = timetableTests.map((t) => ({ testId: t.testId, testDate: t.date, startTime: t.startTime, endTime: t.endTime, remarks: t.remarks }));
    const batchIdsPayload = timetableBatches.filter((b) => b.checked).map((b) => b.id);
    try {
      let resp: any;
      if (timetableMode === "edit" && editingTemplateId) {
        resp = await TestStructureService.updateTestTimetable({
          templateId: editingTemplateId,
          name: timetableName,
          remarks: timetableRemarks,
          tests: testsPayload,
          batchIds: batchIdsPayload,
        });
      } else {
        resp = await TestStructureService.saveTestTimetable({
          programId: timetableData.programId,
          termId: timetableData.termId,
          yearId: timetableData.yearId,
          categoryId: timetableData.categoryId,
          name: timetableName,
          remarks: timetableRemarks,
          tests: testsPayload,
          batchIds: batchIdsPayload,
        });
      }
      if (resp.success) {
        setTimetableMsg({ type: "success", text: resp.message });
        if (programId && yearId) {
          const fresh = await TestStructureService.getStructure(programId, yearId);
          setData(fresh);
        }
        // Reload timetable data to show updated existing schedules
        const refreshed = await TestStructureService.getTestTimetable(timetableData.programId, timetableData.termId, timetableData.categoryId, timetableData.yearId);
        setTimetableData(refreshed);
        setTimetableMode("view");
      } else {
        setTimetableMsg({ type: "error", text: resp.message || "Failed" });
      }
    } catch (e: any) {
      setTimetableMsg({ type: "error", text: e?.response?.data?.message || e?.response?.data?.error || "Failed to save timetable." });
    }
    setTimetableSaving(false);
  };

  // ── Program Level Publish handlers ──
  const openPublishModal = async () => {
    if (!programId || !yearId) return;
    setShowPublishModal(true);
    setPublishBusy(false);
    try {
      const status = await TestStructureService.getPublishStatus(programId, yearId);
      setPublishStatus(status);
    } catch { setPublishStatus({ publishToStaff: false, publishToStudent: false }); }
  };

  const handlePublishToggle = async (publishType: 1 | 2) => {
    if (!programId || !yearId) return;
    setPublishBusy(true);
    try {
      const resp = await TestStructureService.programPublish(programId, yearId, publishType);
      if (resp.success && resp.data) setPublishStatus(resp.data);
    } catch { /* ignore */ }
    setPublishBusy(false);
  };

  // ── Borrow Test Rules handlers (clone Config from another year) ──
  const openBorrowModal = async () => {
    if (!programId || !yearId) return;
    setShowBorrowModal(true);
    setSelectedCloneYearId(null);
    setBorrowMsg(null);
    setBorrowBusy(true);
    try {
      const years = await TestStructureService.getBorrowYears(programId, yearId);
      setBorrowYears(years);
    } catch { setBorrowYears([]); }
    setBorrowBusy(false);
  };

  const handleBorrowSave = async () => {
    if (!programId || !yearId || !selectedCloneYearId) return;
    setBorrowBusy(true);
    setBorrowMsg(null);
    try {
      const resp = await TestStructureService.applyBorrowedRules(programId, yearId, selectedCloneYearId);
      if (resp.success) {
        setBorrowMsg({ type: "success", text: resp.message || "Copied the configuration successfully." });
        // Refresh the matrix
        const fresh = await TestStructureService.getStructure(programId, yearId);
        setData(fresh);
      } else {
        setBorrowMsg({ type: "error", text: resp.message || "Failed to copy configuration." });
      }
    } catch (e: any) {
      setBorrowMsg({ type: "error", text: e?.response?.data?.message || "Failed to copy configuration." });
    }
    setBorrowBusy(false);
  };

  // ── Pass Marks handlers ──
  const openPassMarksModal = async (termId: number, catId: number) => {
    if (!programId || !yearId) return;
    setShowPassMarksModal(true);
    setPassMarksLoading(true);
    setPassMarksMsg(null);
    setPassMarksChecked(new Set());
    try {
      const data = await TestStructureService.getTestsWithThreshold(programId, yearId, termId, catId);
      setPassMarksTests(data.tests || []);
    } catch { setPassMarksTests([]); }
    setPassMarksLoading(false);
  };

  const handlePassMarksSave = async () => {
    if (passMarksChecked.size === 0) {
      setPassMarksMsg({ type: "error", text: "Select at least a Test." });
      return;
    }
    setPassMarksSaving(true);
    setPassMarksMsg(null);
    const payload = passMarksTests
      .filter((t) => passMarksChecked.has(t.testId))
      .map((t) => ({ sections: t.sections.map((s) => ({ sectionId: s.sectionId, threshold: s.threshold, maxMarks: s.maxMarks })) }));
    try {
      const resp = await TestStructureService.updateThreshold(payload);
      setPassMarksMsg({ type: resp.success ? "success" : "error", text: resp.message });
    } catch (e: any) {
      setPassMarksMsg({ type: "error", text: e?.response?.data?.message || "Failed to save." });
    }
    setPassMarksSaving(false);
  };

  const updatePassMarksSection = (testId: number, sectionId: number, field: string, value: number) => {
    setPassMarksTests((prev) =>
      prev.map((t) =>
        t.testId === testId
          ? { ...t, sections: t.sections.map((s) => s.sectionId === sectionId ? { ...s, [field]: value } : s) }
          : t,
      ),
    );
  };

  // ── Reallocate / Month-Year handlers ──
  const openConfirmAction = (type: "reallocate" | "monthYear", termId: number, categoryId: number) => {
    setConfirmAction({ type, termId, categoryId });
    setConfirmMsg(null);
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!programId || !yearId || !confirmAction) return;
    setConfirmBusy(true);
    setConfirmMsg(null);
    try {
      let resp: any;
      if (confirmAction.type === "reallocate") {
        resp = await TestStructureService.reallocateMarks(programId, confirmAction.termId, confirmAction.categoryId, yearId);
      } else {
        resp = await TestStructureService.computeMonthAndYearOfPassing(programId, confirmAction.termId, confirmAction.categoryId, yearId);
      }
      setConfirmMsg({ type: resp.success ? "success" : "error", text: resp.message });
    } catch (e: any) {
      setConfirmMsg({ type: "error", text: e?.response?.data?.message || "Operation failed." });
    }
    setConfirmBusy(false);
  };

  // ── Merge handler ──
  // The configured rule's mergeStyle (set in ConfigEditorModal) determines
  // what this button does:
  //   "merge2" (default) → open the Quick Quiz Maker merge-tests wizard in a
  //                        new tab, prefilled with source test IDs and the
  //                        target category locked.
  //   "merge"            → run the existing server-side test-structure mergeTest
  //                        endpoint in-place (no wizard, refreshes the grid).
  // Button label is always "Merge" regardless of style.
  const handleMerge = async (termId: number, categoryId: number, courseId: number) => {
    if (!programId || !yearId) return;
    const key = `${termId}_${categoryId}_${courseId}`;
    setMergeBusyKey(key);
    setMergeAllMsg(null);
    try {
      const cfg = await TestStructureService.getCatConfig(programId, yearId, termId, categoryId);
      const sourceCfg = cfg?.sourceCategoriesForConsolidation;
      const sources = sourceCfg?.source as Array<{ id: string | number }> | undefined;
      const sourceCategoryIds = (sources || [])
        .map((s) => Number(s.id))
        .filter((n) => Number.isInteger(n) && n > 0);
      if (sourceCategoryIds.length === 0) return;

      // Default to "merge2" when the field is absent (older rules) so existing
      // configs keep the wizard behaviour everyone is used to.
      const style = sourceCfg?.mergeStyle === "merge" ? "merge" : "merge2";

      // Path 1 — server-side merge. Mirrors the old "Merge at Once and All"
      // path the test-structure controller already implements.
      if (style === "merge") {
        const resp = await TestStructureService.mergeTest(programId, courseId, termId, categoryId, yearId, "individual");
        setMergeAllMsg({ type: resp.type === 1 ? "success" : "error", text: resp.message });
        if (resp.type === 1) {
          if (resp.mergeLogic === 2 && resp.computeGrade) {
            openGradeModal(termId, categoryId);
          }
          const fresh = await TestStructureService.getStructure(programId, yearId);
          setData(fresh);
        }
        return;
      }

      // Path 2 — wizard navigation (merge2). Collect source test IDs from the
      // already-loaded grid data and open the Quick Quiz Maker in a new tab.
      const term = data?.terms?.find((t) => t.id === termId);
      const course = term?.courses?.find((c) => c.id === courseId);
      if (!course) return;

      const sourceTestIds: number[] = [];
      for (const srcCatId of sourceCategoryIds) {
        const cellTests = course.cells[String(srcCatId)] || [];
        for (const t of cellTests) {
          if (Number.isInteger(t.testId) && t.testId > 0) sourceTestIds.push(t.testId);
        }
      }

      if (sourceTestIds.length === 0) {
        setMergeAllMsg({
          type: "error",
          text: "The configured source categories have no tests for this module yet — add at least one test in a source category before merging.",
        });
        return;
      }

      const targetCategoryName = (data?.categories || []).find((c) => c.id === categoryId)?.name || "";
      const composedTestName = targetCategoryName
        ? `${course.name} - ${targetCategoryName}`
        : course.name;
      const qs = new URLSearchParams();
      qs.set("mode", "merge-tests");
      qs.set("sourceTestIds", sourceTestIds.join(","));
      qs.set("targetCategoryId", String(categoryId));
      if (targetCategoryName) qs.set("targetCategoryName", targetCategoryName);
      if (composedTestName) qs.set("testName", composedTestName);
      // Open in a new tab so the test-structure page stays available for the
      // admin to issue more merges across other modules without re-navigating.
      window.open(`/quick-quiz-maker?${qs.toString()}`, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setMergeAllMsg({ type: "error", text: e?.response?.data?.message || e?.message || "Failed to start merge." });
    } finally {
      setMergeBusyKey(null);
    }
  };

  const handleMergeAll = async (termId: number, categoryId: number) => {
    if (!programId || !yearId) return;
    const key = `all_${termId}_${categoryId}`;
    setMergeBusyKey(key);
    setMergeAllMsg(null);
    try {
      const resp = await TestStructureService.mergeTest(programId, 0, termId, categoryId, yearId, "all");
      setMergeAllMsg({ type: resp.type === 1 ? "success" : "error", text: resp.message });
      if (resp.type === 1) {
        if (resp.mergeLogic === 2 && resp.computeGrade) {
          openGradeModal(termId, categoryId);
        }
        const fresh = await TestStructureService.getStructure(programId, yearId);
        setData(fresh);
      }
    } catch (e: any) {
      setMergeAllMsg({ type: "error", text: e?.response?.data?.message || "Merge failed." });
    }
    setMergeBusyKey(null);
  };

  // ── Compute Grade handlers ──
  const loadGradeBoundaries = async (termId: number, categoryId: number, typeId: number) => {
    if (!programId || !yearId) return;
    setGradeBoundariesLoading(true);
    try {
      const data = await TestStructureService.getGradeBoundaries(programId, yearId, termId, categoryId, typeId);
      setGradeBoundaries(data.tests || []);
    } catch { setGradeBoundaries([]); }
    setGradeBoundariesLoading(false);
  };

  const openGradeModal = (termId: number, categoryId: number) => {
    setGradeTarget({ termId, categoryId });
    setGradeTypeId(1);
    setGradeMsg(null);
    setEraseGradeBoundaries(false);
    setGradeBoundaries([]);
    setShowGradeModal(true);
    loadGradeBoundaries(termId, categoryId, 1);
  };

  const handleGradeTypeChange = (newTypeId: number) => {
    setGradeTypeId(newTypeId);
    if (gradeTarget) loadGradeBoundaries(gradeTarget.termId, gradeTarget.categoryId, newTypeId);
  };

  const handleComputeGrade = async () => {
    if (!programId || !yearId || !gradeTarget) return;
    setGradeBusy(true);
    setGradeMsg(null);
    try {
      const resp = await TestStructureService.computeGrade(programId, gradeTarget.termId, gradeTarget.categoryId, yearId, gradeTypeId, eraseGradeBoundaries);
      setGradeMsg({ type: resp.success ? "success" : "error", text: resp.message });
    } catch (e: any) {
      setGradeMsg({ type: "error", text: e?.response?.data?.message || "Failed to compute grade." });
    }
    setGradeBusy(false);
  };

  // ── SGPA/CGPA handlers ──
  const openSgpaCgpaModal = (termId: number, categoryId: number) => {
    setSgpaCgpaTarget({ termId, categoryId });
    setSgpaCgpaMsg(null);
    setShowSgpaCgpaModal(true);
  };

  const handleComputeSgpaCgpa = async (mode: "sgpa" | "cgpa") => {
    if (!programId || !yearId || !sgpaCgpaTarget) return;
    setSgpaCgpaBusyMode(mode);
    setSgpaCgpaMsg(null);
    try {
      let resp: any;
      if (mode === "sgpa") {
        resp = await TestStructureService.computeSgpa(programId, sgpaCgpaTarget.termId, sgpaCgpaTarget.categoryId, yearId);
      } else {
        resp = await TestStructureService.computeCgpa(programId, sgpaCgpaTarget.termId, yearId);
      }
      setSgpaCgpaMsg({ type: resp.success ? "success" : "error", text: resp.message });
    } catch (e: any) {
      setSgpaCgpaMsg({ type: "error", text: e?.response?.data?.message || "Operation failed." });
    }
    setSgpaCgpaBusyMode(null);
  };

  // ── Publish Report handlers ──
  const openPublishReportModal = async (termId: number, categoryId: number) => {
    if (!programId || !yearId) return;
    setPublishReportTarget({ termId, categoryId });
    setPublishReportStatus(null);
    setShowPublishReportModal(true);
    try {
      const status = await TestStructureService.getPublishReportStatus(programId, termId, yearId);
      setPublishReportStatus(status);
    } catch { setPublishReportStatus({ publishToStaff: false, publishToStudent: false }); }
  };

  const handlePublishReportToggle = async (publishType: number) => {
    if (!programId || !yearId || !publishReportTarget) return;
    setPublishReportBusy(true);
    try {
      const resp = await TestStructureService.publishReport(programId, publishReportTarget.termId, yearId, publishType);
      if (resp.success && resp.data) setPublishReportStatus(resp.data);
    } catch { /* ignore */ }
    setPublishReportBusy(false);
  };

  // ── Exam Dates handlers ──
  const openExamDatesModal = async (termId: number) => {
    if (!programId || !yearId) return;
    setExamDatesTermId(termId);
    setShowExamDatesModal(true);
    setExamDatesLoading(true);
    setExamDatesMsg(null);
    try {
      const data = await TestStructureService.getExamDates(programId, yearId, termId);
      setExamDatesBatches(data.batches || []);
      setExamDatesIndexes(data.indexes || []);
      const rows = (data.responses || []).map((r: any) => ({
        mappingId: r.mappingId,
        batchId: r.batchId,
        startDate: r.startDate ? new Date(r.startDate).toISOString().slice(0, 10) : "",
        endDate: r.endDate ? new Date(r.endDate).toISOString().slice(0, 10) : "",
        indexId: r.indexId || 0,
      }));
      setExamDatesRows(rows);
    } catch {
      setExamDatesBatches([]);
      setExamDatesIndexes([]);
      setExamDatesRows([]);
    }
    setExamDatesLoading(false);
  };

  const addExamDateRow = () => {
    setExamDatesRows((prev) => [...prev, { mappingId: 0, batchId: 0, startDate: "", endDate: "", indexId: 0 }]);
  };

  const updateExamDateRow = (idx: number, field: string, val: any) => {
    setExamDatesRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  const removeExamDateRow = (idx: number) => {
    setExamDatesRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleExamDatesSave = async () => {
    if (!programId || !yearId) return;
    setExamDatesSaving(true);
    setExamDatesMsg(null);
    const payload = examDatesRows.map((r) => ({
      mappingId: r.mappingId,
      batchId: r.batchId,
      termId: examDatesTermId,
      yearId,
      startDate: r.startDate || null,
      endDate: r.endDate || null,
      indexId: r.indexId,
    }));
    try {
      const resp = await TestStructureService.saveExamDates(payload);
      setExamDatesMsg({ type: resp.success ? "success" : "error", text: resp.message });
    } catch (e: any) {
      setExamDatesMsg({ type: "error", text: e?.response?.data?.message || "Failed to save." });
    }
    setExamDatesSaving(false);
  };

  // ── Category Config (Settings) handlers ──
  const openCatConfigModal = async (termId: number, termName: string, catId: number, catName: string) => {
    if (!programId || !yearId) return;
    setCatConfigTarget({ termId, termName, categoryId: catId, categoryName: catName });
    setShowCatConfigModal(true);
    setCatConfigLoading(true);
    setCatConfigMsg(null);
    setCatConfigMergeLogic("1");
    setCatConfigMappings([]);
    try {
      const data = await TestStructureService.getCatConfig(programId, yearId, termId, catId);
      setCatConfigCategories(data.categories || []);
      setCatConfigCourseTypes2(data.courseTypes2 || []);
      if (data.sourceCategoriesForConsolidation) {
        const sc = data.sourceCategoriesForConsolidation;
        setCatConfigMergeLogic(String(sc.mergeLogic || "1"));
        const sourceList = sc.source || [];
        const mappings = sourceList.map((s: any) => ({
          catId: String(s.id || "0"),
          courseType2Id: String(s.factor?.courseType2Id || "0"),
          value: String(s.factor?.value || ""),
        }));
        setCatConfigMappings([...mappings, { catId: "0", courseType2Id: "0", value: "" }]);
      } else {
        setCatConfigMappings([{ catId: "0", courseType2Id: "0", value: "" }]);
      }
    } catch {
      setCatConfigCategories([]);
      setCatConfigCourseTypes2([]);
      setCatConfigMappings([{ catId: "0", courseType2Id: "0", value: "" }]);
    }
    setCatConfigLoading(false);
  };

  const addCatConfigMapping = () => {
    setCatConfigMappings((prev) => [...prev, { catId: "0", courseType2Id: "0", value: "" }]);
  };

  const updateCatConfigMapping = (idx: number, field: string, val: string) => {
    setCatConfigMappings((prev) => prev.map((m, i) => i === idx ? { ...m, [field]: val } : m));
  };

  const handleCatConfigSave = async () => {
    if (!programId || !yearId || !catConfigTarget) return;
    setCatConfigSaving(true);
    setCatConfigMsg(null);
    const sourceList: any[] = [];
    const catSet = new Set<string>();
    for (const m of catConfigMappings) {
      if (m.catId !== "0" && !catSet.has(m.catId)) {
        const source: any = { id: m.catId };
        if (m.courseType2Id !== "0") {
          source.factor = { courseType2Id: m.courseType2Id, value: m.value };
        }
        sourceList.push(source);
        catSet.add(m.catId);
      }
    }
    const saveData = { mergeLogic: catConfigMergeLogic, source: sourceList };
    try {
      const resp = await TestStructureService.saveCatConfig(programId, yearId, catConfigTarget.termId, catConfigTarget.categoryId, saveData);
      setCatConfigMsg({ type: resp.success ? "success" : "error", text: resp.message });
    } catch (e: any) {
      setCatConfigMsg({ type: "error", text: e?.response?.data?.message || "Failed to save." });
    }
    setCatConfigSaving(false);
  };

  const handleCreateStandardTests = async (scope: "program" | number) => {
    if (!programId || !yearId) return;
    setStandardTestsBusy(scope);
    setStandardTestsMsg(null);
    try {
      const resp = await TestStructureService.createStandardTests(
        programId,
        yearId,
        scope === "program" ? undefined : scope,
      );
      if (resp.success) {
        const created = resp.summary?.created ?? 0;
        const skipped = resp.summary?.skipped ?? 0;
        setStandardTestsMsg({
          type: "success",
          text: `Created ${created} test${created === 1 ? "" : "s"}${skipped ? `, ${skipped} skipped` : ""}.`,
        });
        // Refresh matrix
        const fresh = await TestStructureService.getStructure(programId, yearId);
        setData(fresh);
      } else {
        setStandardTestsMsg({ type: "error", text: resp.message || "Failed to create standard tests" });
      }
    } catch (e: any) {
      setStandardTestsMsg({
        type: "error",
        text: e?.response?.data?.message || e?.message || "Failed to create standard tests",
      });
    } finally {
      setStandardTestsBusy(null);
    }
  };

  // ── Open Entry handlers ──
  const handleOpenEntry = async () => {
    if (!programId || !yearId || !openEntryTarget) return;
    setOpenEntryBusy(true);
    setOpenEntryMsg(null);
    try {
      const resp = await TestStructureService.openDataEntry(programId, yearId, openEntryTarget.termId, openEntryTarget.categoryId);
      if (resp.success) {
        setOpenEntryMsg({ type: "success", text: resp.message });
        // Refresh data entry status and structure
        const [fresh, statusMap] = await Promise.all([
          TestStructureService.getStructure(programId, yearId),
          TestStructureService.getDataEntryStatus(programId, yearId),
        ]);
        setData(fresh);
        setDataEntryStatus(statusMap);
      } else {
        setOpenEntryMsg({ type: "error", text: resp.message || "Failed to open entry" });
      }
    } catch (e: any) {
      setOpenEntryMsg({ type: "error", text: e?.response?.data?.message || "Failed to open entry" });
    }
    setOpenEntryBusy(false);
  };

  const openCreateForCell = (
    termId: number,
    courseId: number,
    category: TestStructureCategory,
  ) => {
    if (!programId) return;
    const term = data?.terms.find((t) => t.id === termId);
    const course = term?.courses.find((c) => c.id === courseId);
    const qs = new URLSearchParams({
      mode: "create",
      returnTo: "test-structure",
      categoryId: String(category.id),
      categoryName: category.name,
      courseId: String(courseId),
      courseName: course?.name || "",
      programId: String(programId),
      programName: data?.program.name || "",
      termId: String(termId),
      termName: term?.displayName || term?.name || "",
    });
    if (yearId) {
      qs.set("yearId", String(yearId));
      qs.set("yearName", data?.year.name || "");
    }
    window.open(`/quick-quiz-maker?${qs.toString()}`, "_blank");
  };

  // ==================== Render ====================

  const hasFilters = programId && yearId;
  const terms = data?.terms || [];
  const categories = data?.categories || [];

  return (
    <div className="test-structure-page">
      {/* Header */}
      <div className="ts-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 className="ts-title" style={{ margin: 0 }}>Test Structure</h1>
            <ElementGuard pageKey={PAGE_KEY} adminOnly>
              <button
                onClick={() => setShowAccessControlModal(true)}
                title="Page access control — roles & permissions"
                style={{
                  width: 32,
                  height: 32,
                  padding: 0,
                  borderRadius: 8,
                  border: "1px solid var(--border-default)",
                  background: "var(--surface-card)",
                  color: "var(--brand-primary)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  margin: 2,
                }}
              >
                <Settings size={16} />
              </button>
            </ElementGuard>
          </div>
          {data && (
            <div className="ts-subtitle">
              {data.program.name} · {data.year.name}
            </div>
          )}
        </div>

        {hasFilters && (
          <div className="ts-header-actions">
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.configEditor">
              <button className="ts-action-btn" style={{ background: "var(--brand-accent)", color: "var(--text-on-brand)", margin: 2 }} onClick={() => setShowConfigModal(true)}>
                <Settings size={14} /> Config Editor
              </button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.borrowTestRules">
              <button className="ts-action-btn ts-btn-borrow" style={{ margin: 2 }} onClick={openBorrowModal}>
                <Copy size={14} /> Borrow Test Rules
              </button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.programLevelPublish">
              <button className="ts-action-btn ts-btn-publish" style={{ margin: 2 }} onClick={openPublishModal}>
                <Send size={14} /> Program Level Publish
              </button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.createStandardTests">
              <button
                className="ts-action-btn ts-btn-standard"
                style={{ margin: 2 }}
                disabled={standardTestsBusy !== null}
                onClick={() => handleCreateStandardTests("program")}
              >
                <FilePlus size={14} /> {standardTestsBusy === "program" ? "Creating…" : "Create Standard Tests"}
              </button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.createNew">
              <button
                className="ts-action-btn ts-btn-create"
                style={{ margin: 2 }}
                onClick={() => history.push("/quick-quiz-maker?mode=create")}
              >
                <Plus size={14} /> Create New
              </button>
            </ElementGuard>
          </div>
        )}
      </div>

      <ElementGuard pageKey={PAGE_KEY} adminOnly>
        <AccessControlEditorModal
          pageKey={PAGE_KEY}
          show={showAccessControlModal}
          onClose={() => setShowAccessControlModal(false)}
        />
      </ElementGuard>

      {standardTestsMsg && (
        <div className={`ts-inline-msg ts-inline-${standardTestsMsg.type}`}>
          {standardTestsMsg.text}
          <button className="ts-inline-close" onClick={() => setStandardTestsMsg(null)}>×</button>
        </div>
      )}

      {/* Toolbar */}
      <div className="ts-toolbar">
        <div className="ts-field">
          <label>Course Type</label>
          <select
            value={courseTypeId ?? ""}
            onChange={(e) => {
              setCourseTypeId(e.target.value ? Number(e.target.value) : null);
              setProgramId(null);
              setYearId(null);
            }}
          >
            <option value="">All</option>
            {courseTypes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="ts-field">
          <label>Program</label>
          <select
            value={programId ?? ""}
            disabled={programsLoading}
            onChange={(e) => {
              setProgramId(e.target.value ? Number(e.target.value) : null);
              setYearId(null);
            }}
          >
            <option value="">
              {programsLoading ? "Loading…" : "Select Program"}
            </option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="ts-field">
          <label>Year</label>
          <select
            value={yearId ?? ""}
            disabled={!programId || yearsLoading}
            onChange={(e) => setYearId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">
              {!programId ? "Pick program first" : yearsLoading ? "Loading…" : "Select Year"}
            </option>
            {years.map((y) => (
              <option key={y.id} value={y.id}>{y.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* States */}
      {!hasFilters && (
        <div className="ts-empty-card">
          <div className="ts-empty-icon">📘</div>
          <div className="ts-empty-title">Pick a program and year</div>
          <div className="ts-empty-sub">Choose a program and academic year to see its test structure.</div>
        </div>
      )}

      {hasFilters && loading && (
        <div className="ts-loading">
          <Spinner role="status" animation="border" size="sm" /> Loading test structure…
        </div>
      )}

      {hasFilters && !loading && error && (
        <div className="ts-error">{error}</div>
      )}

      {hasFilters && !loading && !error && data && terms.length === 0 && (
        <div className="ts-empty-card">
          <div className="ts-empty-title">No terms configured</div>
          <div className="ts-empty-sub">This program doesn&apos;t have academic terms set up yet.</div>
        </div>
      )}

      {hasFilters && !loading && !error && data && terms.length > 0 && categories.length === 0 && (
        <div className="ts-empty-card">
          <div className="ts-empty-title">No test categories</div>
          <div className="ts-empty-sub">Configure test categories for this program to populate the matrix.</div>
        </div>
      )}

      {/* Term sections */}
      {hasFilters && !loading && !error && data && categories.length > 0 &&
        terms.map((term) => (
          <section key={term.id} className="ts-term-card">
            <header className="ts-term-header">
              <div className="ts-term-title">
                <span className="ts-term-name">{term.displayName || term.name}</span>
                <span className="ts-term-meta">{term.totalStudents} students</span>
              </div>
              <div className="ts-term-actions">
                <button
                  className="ts-action-btn ts-btn-term-standard"
                  disabled={standardTestsBusy !== null}
                  onClick={() => handleCreateStandardTests(term.id)}
                >
                  <FilePlus size={13} /> {standardTestsBusy === term.id ? "Creating…" : "Create Standard Tests"}
                </button>
              </div>
            </header>

            <div className="ts-matrix-scroll">
              <table className="ts-matrix">
                <colgroup>
                  <col className="ts-col-course" />
                  {categories.map((c) => (
                    <col key={c.id} className="ts-col-category" />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="ts-matrix-corner">Course</th>
                    {categories.map((cat) => (
                      <th key={cat.id} className="ts-matrix-head-cell">
                        <div className="ts-category-head">
                          <span className="ts-category-name">{cat.name}</span>
                          <div className="ts-category-actions">
                            <ElementGuard pageKey={PAGE_KEY} elementKey="action.scheduleTimetable">
                              <SoonButton label={`Schedule ${cat.name}`} icon={<Calendar size={14} />} onClick={() => openTimetable(term.id, cat.id)} />
                            </ElementGuard>
                            {cat.deleteTests && (
                              <ElementGuard pageKey={PAGE_KEY} elementKey="action.deleteTests">
                                <SoonButton label="Delete Tests" icon={<Trash2 size={14} />} />
                              </ElementGuard>
                            )}
                            {(cat.sourceCategoriesForConsolidation || (data?.consolidationByTerm?.[term.id] || []).includes(cat.id)) && (
                              <>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.mergeAtOnce">
                                  <SoonButton label="Merge at Once and All" icon={
                                    mergeBusyKey === `all_${term.id}_${cat.id}` ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> : <Merge size={14} />
                                  } onClick={() => handleMergeAll(term.id, cat.id)} />
                                </ElementGuard>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.categorySetting">
                                  <SoonButton label="Setting" icon={<Settings size={14} style={{ color: "var(--status-success)" }} />} onClick={() => openCatConfigModal(term.id, term.displayName || term.name, cat.id, cat.name)} />
                                </ElementGuard>
                              </>
                            )}
                            <ElementGuard pageKey={PAGE_KEY} elementKey="action.dvsPattern">
                              <SoonButton label={`DVS Pattern — ${cat.name}`} icon={<FileCheck size={14} />} onClick={() => openDvsModal(term.id, term.displayName || term.name, cat.id, cat.name)} />
                            </ElementGuard>
                            {cat.reallocateMarks && (
                              <ElementGuard pageKey={PAGE_KEY} elementKey="action.reallocateMarks">
                                <SoonButton label="Re allocate Marks" icon={<RefreshCw size={14} />} onClick={() => openConfirmAction("reallocate", term.id, cat.id)} />
                              </ElementGuard>
                            )}
                            {cat.computeStudentGrade && (
                              <>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.passMarks">
                                  <SoonButton label="Pass Marks" icon={<BarChart3 size={14} />} onClick={() => openPassMarksModal(term.id, cat.id)} />
                                </ElementGuard>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.computeGrade">
                                  <SoonButton label="Compute Grade" icon={<CheckCircle size={14} />} onClick={() => openGradeModal(term.id, cat.id)} />
                                </ElementGuard>
                              </>
                            )}
                            {cat.graceMarks && (
                              <ElementGuard pageKey={PAGE_KEY} elementKey="action.graceMarks">
                                <SoonButton label="Grace Marks" icon={<Repeat size={14} />} />
                              </ElementGuard>
                            )}
                            {cat.trackDataEntry && (
                              <ElementGuard pageKey={PAGE_KEY} elementKey="action.openEntry">
                                <OverlayTrigger placement="bottom" overlay={
                                  <Tooltip>{dataEntryStatus[`${term.id}_${cat.id}`] || "Open Entry"}</Tooltip>
                                }>
                                  <button
                                    className={`btn btn-sm ts-icon-btn ${dataEntryStatus[`${term.id}_${cat.id}`] ? "ts-entry-opened" : "ts-entry-closed"}`}
                                    onClick={() => {
                                      if (dataEntryStatus[`${term.id}_${cat.id}`]) {
                                        window.open(`/test-data-entry-reporting?termId=${term.id}&categoryId=${cat.id}&yearId=${yearId}`, "_blank");
                                      } else {
                                        setOpenEntryTarget({ termId: term.id, termName: term.displayName || term.name, categoryId: cat.id, categoryName: cat.name });
                                        setOpenEntryMsg(null);
                                        setShowOpenEntryModal(true);
                                      }
                                    }}
                                  >
                                    <Save size={14} />
                                  </button>
                                </OverlayTrigger>
                              </ElementGuard>
                            )}
                            {cat.computeSgpa && (
                              <>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.computeSgpaCgpa">
                                  <SoonButton label="Compute SGPA / CGPA" icon={<CheckSquare size={14} />} onClick={() => openSgpaCgpaModal(term.id, cat.id)} />
                                </ElementGuard>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.publishMarksReport">
                                  <SoonButton label="Publish Marks Report" icon={<Flag size={14} />} onClick={() => openPublishReportModal(term.id, cat.id)} />
                                </ElementGuard>
                              </>
                            )}
                            {cat.computeStudentGrade && (
                              <>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.examDates">
                                  <SoonButton label="Exam Dates" icon={<BookOpen size={14} style={{ color: "var(--status-success)" }} />} onClick={() => openExamDatesModal(term.id)} />
                                </ElementGuard>
                                <ElementGuard pageKey={PAGE_KEY} elementKey="action.monthYearPassing">
                                  <SoonButton label="Month and Year of passing" icon={<ListChecks size={14} style={{ color: "var(--status-success)" }} />} onClick={() => openConfirmAction("monthYear", term.id, cat.id)} />
                                </ElementGuard>
                              </>
                            )}
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {term.courses.length === 0 ? (
                    <tr>
                      <td
                        className="ts-term-empty-row"
                        colSpan={categories.length + 1}
                      >
                        No courses configured for this term.
                      </td>
                    </tr>
                  ) : (
                    term.courses.map((crs) => (
                      <tr key={crs.id}>
                        <td className="ts-course-cell">
                          <div className="ts-course-name">{crs.name}</div>
                          {crs.code && <div className="ts-course-code">[{crs.code}]</div>}
                        </td>
                        {categories.map((cat) => {
                          const cellTests = crs.cells[String(cat.id)] || [];
                          // A category is "mergeable" if it has consolidation rules either
                          // at the global category level (test_category_master.Config) or at
                          // the per-term level (program_term_year_properties.JsonConfig).
                          const termConsolidationCats = data?.consolidationByTerm?.[term.id] || [];
                          const canMerge = cat.sourceCategoriesForConsolidation === true
                            || termConsolidationCats.includes(cat.id);
                          return (
                            <td key={cat.id} className="ts-matrix-cell">
                              {cellTests.length === 0 ? (
                                <div className="ts-cell-stack">
                                  <button
                                    className="ts-cell-empty"
                                    onClick={() => openCreateForCell(term.id, crs.id, cat)}
                                  >
                                    Add Test
                                  </button>
                                  {canMerge && (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Merge</Tooltip>}>
                                      <button
                                        disabled={mergeBusyKey !== null}
                                        onClick={() => handleMerge(term.id, cat.id, crs.id)}
                                        style={{
                                          marginTop: 4, padding: "4px 8px", borderRadius: 6, border: "none",
                                          cursor: "pointer", backgroundColor: "var(--status-success)", color: "var(--text-on-brand)",
                                          display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.72rem", fontWeight: 600,
                                        }}
                                      >
                                        {mergeBusyKey === `${term.id}_${cat.id}_${crs.id}` ? (
                                          <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                                        ) : (
                                          <Merge size={12} />
                                        )}
                                        Merge
                                      </button>
                                    </OverlayTrigger>
                                  )}
                                </div>
                              ) : (
                                <div className="ts-cell-stack">
                                  {cellTests.map((t) => {
                                    const tone = coverageTone(t.attempted, t.denominator);
                                    return (
                                      <div
                                        key={t.testId}
                                        className={`ts-test-card ${t.isBacklog ? "is-backlog" : ""}`}
                                      >
                                        <button
                                          className="ts-card-close"
                                          onClick={() => setRemoveTarget(t)}
                                          title="Remove from program"
                                        >
                                          ×
                                        </button>
                                        <a
                                          className="ts-card-title"
                                          href={`/quick-quiz-maker?mode=view&testId=${t.testId}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={t.testName}
                                        >
                                          {t.testName}
                                        </a>
                                        <div className="ts-card-meta">
                                          {t.testDate && <span>{formatDate(t.testDate)}</span>}
                                          {t.isBacklog && <span className="ts-backlog-pill">Backlog</span>}
                                        </div>
                                        <div className={`ts-coverage ts-coverage-${tone}`}>
                                          {t.coverage}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {canMerge && (
                                    // Target cell already has a merged test — keep the button
                                    // visible (so the rule is still discoverable) but disable it
                                    // to prevent creating a second merged test for the same
                                    // (module, category) tuple. Tooltip explains why.
                                    <OverlayTrigger placement="top" overlay={<Tooltip>A merged test already exists for this module &amp; category. Delete it before merging again.</Tooltip>}>
                                      <span style={{ display: "inline-flex" }}>
                                        <button
                                          disabled
                                          style={{
                                            marginBottom: 4, padding: "4px 8px", borderRadius: 6, border: "none",
                                            cursor: "not-allowed", backgroundColor: "#94a3b8", color: "var(--text-on-brand)",
                                            display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.72rem", fontWeight: 600,
                                            opacity: 0.85,
                                          }}
                                        >
                                          <Merge size={12} />
                                          Merge
                                        </button>
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                  <button
                                    className="ts-cell-add-more"
                                    onClick={() => openCreateForCell(term.id, crs.id, cat)}
                                  >
                                    + Add
                                  </button>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ))}

      {/* Add existing test modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Add Existing Test to Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            className="ts-search-input"
            placeholder="Search tests by name…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            autoFocus
          />
          {addMsg && (
            <div className={`ts-alert ts-alert-${addMsg.type}`}>{addMsg.text}</div>
          )}
          {searching && (
            <div className="ts-searching">
              <Spinner role="status" animation="border" size="sm" />
            </div>
          )}
          {!searching && searchResults.length > 0 && (
            <div className="ts-search-results">
              {searchResults.map((r: any) => (
                <div key={r.testId} className="ts-search-row" onClick={() => handlePickTest(r)}>
                  <span>
                    <strong>{r.name}</strong>{" "}
                    <span className="ts-muted">
                      ({r.categoryName || "No Cat"}) — {formatDate(r.testDate)}
                    </span>
                  </span>
                  <span className="ts-row-add">Pick →</span>
                </div>
              ))}
            </div>
          )}
          {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div className="ts-search-empty">No tests found.</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Pick-course modal */}
      <Modal show={!!pendingAdd} onHide={() => setPendingAdd(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Select course for &ldquo;{pendingAdd?.testName}&rdquo;
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingAdd && pendingAdd.courses.length === 0 && (
            <div className="ts-searching">
              <Spinner role="status" animation="border" size="sm" /> Loading courses…
            </div>
          )}
          {pendingAdd && pendingAdd.courses.length > 0 && (
            <>
              <p style={{ fontSize: "0.85rem" }}>
                Which course in this program should the test be mapped to?
              </p>
              <select
                className="ts-select-full"
                value={pendingAdd.selectedCourseId ?? ""}
                onChange={(e) =>
                  setPendingAdd((prev) =>
                    prev
                      ? {
                          ...prev,
                          selectedCourseId: e.target.value ? Number(e.target.value) : null,
                        }
                      : null,
                  )
                }
              >
                <option value="">Select course…</option>
                {pendingAdd.courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setPendingAdd(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!pendingAdd?.selectedCourseId || addBusy}
            onClick={handleConfirmAdd}
          >
            {addBusy ? <Spinner role="status" animation="border" size="sm" /> : "Add to Program"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Program Config Editor */}
      {programId && yearId && (
        <ConfigEditorModal
          show={showConfigModal}
          onHide={() => setShowConfigModal(false)}
          programId={programId}
          yearId={yearId}
        />
      )}

      {/* Remove confirm */}
      <Modal show={!!removeTarget} onHide={() => setRemoveTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Remove Test from Program</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          Remove <strong>{removeTarget?.testName}</strong> from{" "}
          <strong>{removeTarget?.courseName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleRemove} disabled={removing}>
            {removing ? <Spinner role="status" animation="border" size="sm" /> : "Remove"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DVS Pattern Modal */}
      <Modal show={showDvsModal} onHide={() => setShowDvsModal(false)} centered>
        <Modal.Header closeButton style={{ background: "var(--brand-primary)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Dvs Patterns for {dvsTermCat?.termName} :: {dvsTermCat?.categoryName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ fontWeight: 500, whiteSpace: "nowrap" }}>Dvs Pattern</label>
            <select
              style={{ flex: 1, padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border-strong)" }}
              value={dvsSelected}
              onChange={(e) => setDvsSelected(e.target.value)}
              disabled={dvsBusy}
            >
              <option value="-1">#SELECT#</option>
              {dvsPatterns.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
            {dvsBusy && <Spinner animation="border" size="sm" />}
          </div>
          {dvsMsg && (
            <div style={{
              marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: dvsMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: dvsMsg.type === "success" ? "#166534" : "#991b1b",
            }}>
              {dvsMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowDvsModal(false)}>
            Close
          </Button>
          <Button variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }} disabled={dvsBusy} onClick={handleDvsSave}>
            {dvsBusy ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Test Timetable Modal */}
      <Modal show={showTimetableModal} onHide={() => setShowTimetableModal(false)} size="xl" dialogClassName="ts-timetable-modal-full">
        <Modal.Header closeButton style={{ background: "var(--status-warning)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Test Schedule {timetableData ? `— ${timetableData.programName} | ${timetableData.yearName} | ${timetableData.termName} | ${timetableData.categoryName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          {timetableLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" /></div>
          ) : timetableMode === "view" && !timetableLoading ? (
            /* ── View existing timetables ── */
            <>
              {(timetableData?.templates || []).length > 0 ? (
                (timetableData.templates as any[]).map((tmpl: any) => (
                  <div key={tmpl.id} style={{ marginBottom: 20, border: "1px solid var(--border-default)", borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ background: "#f0f9ff", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--border-default)" }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{tmpl.name}</span>
                        <span style={{ color: "var(--text-muted)", marginLeft: 12, fontSize: "0.78rem" }}>{tmpl.startDate} — {tmpl.endDate}</span>
                        {tmpl.remarks && <div style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginTop: 2 }}>{tmpl.remarks}</div>}
                        {tmpl.batches?.length > 0 && (
                          <div style={{ fontSize: "0.75rem", color: "var(--text-body)", marginTop: 2 }}>
                            Sections: {tmpl.batches.map((b: any) => b.name).join(", ")}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => openTimetableEdit(tmpl)}
                        style={{
                          padding: "5px 14px", borderRadius: 5, border: "1px solid var(--brand-primary)",
                          background: "var(--surface-card)", color: "var(--brand-primary)", fontSize: "0.78rem", fontWeight: 600,
                          cursor: "pointer", whiteSpace: "nowrap", marginLeft: 12,
                        }}
                      >
                        Edit
                      </button>
                    </div>
                    {tmpl.slots?.length > 0 ? (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
                        <thead>
                          <tr style={{ background: "var(--surface-page)" }}>
                            <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "1px solid var(--border-default)" }}>Subject</th>
                            <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "1px solid var(--border-default)" }}>Test Name</th>
                            <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "1px solid var(--border-default)" }}>Date</th>
                            <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "1px solid var(--border-default)" }}>Start</th>
                            <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "1px solid var(--border-default)" }}>End</th>
                            <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "1px solid var(--border-default)" }}>Syllabus</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tmpl.slots.map((s: any) => (
                            <tr key={s.slotId} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                              <td style={{ padding: "5px 8px" }}>{s.courseName || "-"}</td>
                              <td style={{ padding: "5px 8px" }}>{s.testName || "-"}</td>
                              <td style={{ padding: "5px 8px" }}>{s.testDate || "-"}</td>
                              <td style={{ padding: "5px 8px" }}>{s.startTime || "-"}</td>
                              <td style={{ padding: "5px 8px" }}>{s.endTime || "-"}</td>
                              <td style={{ padding: "5px 8px" }}>{s.remarks || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ padding: 12, color: "var(--text-faint)", fontSize: "0.8rem" }}>No test slots in this schedule.</div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                  No existing schedules found for this term and category.
                </div>
              )}
              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button
                  onClick={() => setTimetableMode("create")}
                  style={{
                    padding: "10px 28px", borderRadius: 8, border: "none", cursor: "pointer",
                    background: "var(--brand-primary)", color: "var(--text-on-brand)", fontSize: "0.88rem", fontWeight: 600,
                    display: "inline-flex", alignItems: "center", gap: 8,
                    boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                  }}
                >
                  <Plus size={16} /> Create New Timetable
                </button>
              </div>
            </>
          ) : (
            /* ── Create / Edit timetable ── */
            <>
              <div style={{ marginBottom: 8 }}>
                <button style={{ background: "none", border: "none", color: "var(--brand-primary)", cursor: "pointer", fontSize: "0.82rem", padding: 0 }}
                  onClick={() => setTimetableMode("view")}
                >
                  ← Back to existing schedules
                </button>
                <span style={{ marginLeft: 12, fontWeight: 600, fontSize: "0.85rem", color: "var(--text-body)" }}>
                  {timetableMode === "edit" ? "Edit Timetable" : "New Timetable"}
                </span>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, marginRight: 8 }}>Schedule Name:</label>
                <input
                  style={{ padding: "4px 8px", border: "1px solid var(--border-strong)", borderRadius: 4, width: 350 }}
                  value={timetableName}
                  onChange={(e) => setTimetableName(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, marginRight: 8 }}>Remarks:</label>
                <textarea
                  style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border-strong)", borderRadius: 4, fontSize: "0.82rem", minHeight: 50, resize: "vertical" }}
                  placeholder="General remarks for this timetable..."
                  value={timetableRemarks}
                  onChange={(e) => setTimetableRemarks(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                {/* Tests panel */}
                <div style={{ flex: 3 }}>
                  <div style={{ background: "var(--status-danger)", color: "var(--text-on-brand)", padding: "4px 10px", borderRadius: "4px 4px 0 0", fontWeight: 600, fontSize: "0.82rem" }}>Tests</div>
                  <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid var(--border-default)" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
                      <thead>
                        <tr style={{ background: "var(--surface-sunken)", position: "sticky", top: 0 }}>
                          <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Subject</th>
                          <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Test Name</th>
                          <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Test Date</th>
                          <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Start Time</th>
                          <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>End Time</th>
                          <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Syllabus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timetableTests.map((t, idx) => (
                          <tr key={t.testId} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                            <td style={{ padding: "5px 6px" }}>{t.courseName}</td>
                            <td style={{ padding: "5px 6px" }}>{t.name}</td>
                            <td style={{ padding: "5px 6px" }}>
                              <input type="date" style={{ padding: "2px 4px", border: "1px solid var(--border-strong)", borderRadius: 3, width: 120, fontSize: "0.78rem" }}
                                value={t.date ? t.date.split("/").reverse().join("-") : ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const formatted = v ? v.split("-").reverse().join("/") : "";
                                  handleTimetableFieldChange(idx, "date", formatted);
                                }}
                              />
                            </td>
                            <td style={{ padding: "5px 6px" }}>
                              <input type="time" style={{ padding: "2px 4px", border: "1px solid var(--border-strong)", borderRadius: 3, width: 80, fontSize: "0.78rem" }}
                                value={t.startTime} onChange={(e) => handleTimetableFieldChange(idx, "startTime", e.target.value)} />
                            </td>
                            <td style={{ padding: "5px 6px" }}>
                              <input type="time" style={{ padding: "2px 4px", border: "1px solid var(--border-strong)", borderRadius: 3, width: 80, fontSize: "0.78rem" }}
                                value={t.endTime} onChange={(e) => handleTimetableFieldChange(idx, "endTime", e.target.value)} />
                            </td>
                            <td style={{ padding: "5px 6px" }}>
                              <textarea style={{ padding: "2px 4px", border: "1px solid var(--border-strong)", borderRadius: 3, width: 200, minHeight: 32, fontSize: "0.78rem", resize: "vertical" }}
                                value={t.remarks} onChange={(e) => handleTimetableFieldChange(idx, "remarks", e.target.value)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Sections/Batches panel */}
                <div style={{ flex: 1 }}>
                  <div style={{ background: "var(--brand-primary)", color: "var(--text-on-brand)", padding: "4px 10px", borderRadius: "4px 4px 0 0", fontWeight: 600, fontSize: "0.82rem" }}>Section</div>
                  <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid var(--border-default)" }}>
                    <div style={{ padding: "6px 8px", borderBottom: "1px solid var(--border-default)", background: "var(--surface-page)" }}>
                      <label style={{ cursor: "pointer", fontSize: "0.78rem" }}>
                        <input type="checkbox" checked={timetableBatches.length > 0 && timetableBatches.every((b) => b.checked)} onChange={handleBatchToggleAll} style={{ marginRight: 6 }} />
                        Select All
                      </label>
                    </div>
                    {timetableBatches.map((b, idx) => (
                      <div key={b.id} style={{ padding: "5px 8px", borderBottom: "1px solid var(--border-subtle)", fontSize: "0.78rem" }}>
                        <label style={{ cursor: "pointer" }}>
                          <input type="checkbox" checked={b.checked} onChange={() => handleBatchToggle(idx)} style={{ marginRight: 6 }} />
                          {b.name}
                        </label>
                      </div>
                    ))}
                    {timetableBatches.length === 0 && (
                      <div style={{ padding: 8, fontSize: "0.78rem", color: "var(--text-faint)" }}>No batches found</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {timetableMsg && (
            <div style={{
              marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: timetableMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: timetableMsg.type === "success" ? "#166534" : "#991b1b",
            }}>
              {timetableMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowTimetableModal(false)}>
            Close
          </Button>
          {(timetableMode === "create" || timetableMode === "edit") && timetableTests.length > 0 && (
            <Button variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }} disabled={timetableSaving || !timetableName} onClick={handleTimetableSave}>
              {timetableSaving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Program Level Publish */}
      <Modal show={showPublishModal} onHide={() => setShowPublishModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Program Level Publish</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          {publishStatus ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 8, background: publishStatus.publishToStaff ? "#dcfce7" : "#fee2e2" }}>
                <span style={{ fontWeight: 500 }}>Publish to Staff</span>
                <button
                  disabled={publishBusy}
                  onClick={() => handlePublishToggle(1)}
                  style={{
                    minWidth: 130, padding: "6px 16px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
                    backgroundColor: publishStatus.publishToStaff ? "#16a34a" : "#dc2626",
                    color: "var(--text-on-brand)",
                  }}
                >
                  {publishStatus.publishToStaff ? "✓ Published" : "✗ Not Published"}
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 8, background: publishStatus.publishToStudent ? "#dcfce7" : "#fee2e2" }}>
                <span style={{ fontWeight: 500 }}>Publish to Student</span>
                <button
                  disabled={publishBusy}
                  onClick={() => handlePublishToggle(2)}
                  style={{
                    minWidth: 130, padding: "6px 16px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
                    backgroundColor: publishStatus.publishToStudent ? "#16a34a" : "#dc2626",
                    color: "var(--text-on-brand)",
                  }}
                >
                  {publishStatus.publishToStudent ? "✓ Published" : "✗ Not Published"}
                </button>
              </div>
              <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: 0 }}>Click the status button to toggle publish/unpublish.</p>
            </div>
          ) : (
            <Spinner animation="border" size="sm" />
          )}
        </Modal.Body>
      </Modal>

      {/* Borrow Test Rules (clone Config from another year) */}
      <Modal show={showBorrowModal} onHide={() => setShowBorrowModal(false)} centered>
        <Modal.Header closeButton style={{ background: "var(--brand-primary)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Borrow test rules from</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ fontWeight: 500, whiteSpace: "nowrap" }}>Borrow test rules from</label>
            <select
              style={{ flex: 1, padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border-strong)" }}
              value={selectedCloneYearId ?? ""}
              onChange={(e) => setSelectedCloneYearId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">#SELECT#</option>
              {borrowYears.map((y) => (
                <option key={y.yearId} value={y.yearId}>{y.yearName}</option>
              ))}
            </select>
            {borrowBusy && <Spinner animation="border" size="sm" />}
          </div>
          {borrowMsg && (
            <div style={{
              marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: borrowMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: borrowMsg.type === "success" ? "#166534" : "#991b1b",
            }}>
              {borrowMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowBorrowModal(false)}>
            Close
          </Button>
          <Button variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }} disabled={!selectedCloneYearId || borrowBusy} onClick={handleBorrowSave}>
            {borrowBusy ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pass Marks Modal */}
      <Modal show={showPassMarksModal} onHide={() => setShowPassMarksModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Pass Marks</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          {passMarksLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" /></div>
          ) : passMarksTests.length === 0 ? (
            <div style={{ padding: 16, color: "var(--text-muted)" }}>No tests found.</div>
          ) : (
            <div style={{ maxHeight: 500, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ background: "var(--surface-sunken)", position: "sticky", top: 0 }}>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", width: 30 }}>
                      <input type="checkbox"
                        checked={passMarksChecked.size === passMarksTests.length && passMarksTests.length > 0}
                        onChange={() => {
                          if (passMarksChecked.size === passMarksTests.length) {
                            setPassMarksChecked(new Set());
                          } else {
                            setPassMarksChecked(new Set(passMarksTests.map((t) => t.testId)));
                          }
                        }}
                      />
                    </th>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Test</th>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Date</th>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Section</th>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "center" }}>Max Marks</th>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "center" }}>Pass Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {passMarksTests.map((t) =>
                    t.sections.map((s, sIdx) => (
                      <tr key={`${t.testId}-${s.sectionId}`} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                        {sIdx === 0 && (
                          <>
                            <td rowSpan={t.sections.length} style={{ padding: "6px 10px", verticalAlign: "top" }}>
                              <input type="checkbox" checked={passMarksChecked.has(t.testId)}
                                onChange={() => {
                                  setPassMarksChecked((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(t.testId)) next.delete(t.testId); else next.add(t.testId);
                                    return next;
                                  });
                                }}
                              />
                            </td>
                            <td rowSpan={t.sections.length} style={{ padding: "6px 10px", verticalAlign: "top", fontWeight: 500 }}>{t.testName}</td>
                            <td rowSpan={t.sections.length} style={{ padding: "6px 10px", verticalAlign: "top" }}>{t.testDate}</td>
                          </>
                        )}
                        <td style={{ padding: "6px 10px" }}>{s.sectionName}</td>
                        <td style={{ padding: "6px 8px", textAlign: "center" }}>
                          <input type="number" disabled={!passMarksChecked.has(t.testId)}
                            style={{ width: 70, padding: "3px 6px", border: "1px solid var(--border-strong)", borderRadius: 3, textAlign: "center", fontSize: "0.82rem", background: passMarksChecked.has(t.testId) ? "#fff" : "#f3f4f6", color: passMarksChecked.has(t.testId) ? "#111" : "#9ca3af" }}
                            value={s.maxMarks ?? ""} onChange={(e) => updatePassMarksSection(t.testId, s.sectionId, "maxMarks", Number(e.target.value))} />
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "center" }}>
                          <input type="number" disabled={!passMarksChecked.has(t.testId)}
                            style={{ width: 70, padding: "3px 6px", border: "1px solid var(--border-strong)", borderRadius: 3, textAlign: "center", fontSize: "0.82rem", background: passMarksChecked.has(t.testId) ? "#fff" : "#f3f4f6", color: passMarksChecked.has(t.testId) ? "#111" : "#9ca3af" }}
                            value={s.threshold ?? ""} onChange={(e) => updatePassMarksSection(t.testId, s.sectionId, "threshold", Number(e.target.value))} />
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          )}
          {passMarksMsg && (
            <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: passMarksMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: passMarksMsg.type === "success" ? "#166534" : "#991b1b" }}>
              {passMarksMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowPassMarksModal(false)}>Close</Button>
          {passMarksTests.length > 0 && (
            <Button variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }} disabled={passMarksSaving} onClick={handlePassMarksSave}>
              {passMarksSaving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Reallocate / Month-Year Confirm Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {confirmAction?.type === "reallocate" ? "Confirm Re Allocate Marks" : "Confirm"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          {confirmAction?.type === "reallocate" ? (
            <p>Are you sure you want to Re Allocate Marks?</p>
          ) : (
            <>
              <p>Are you sure you want to Compute Month and Year of Passing?</p>
              <p style={{ color: "var(--status-danger)", fontSize: "0.82rem" }}>*NOTE : The data will be overwritten if already computed*</p>
            </>
          )}
          {confirmMsg && (
            <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: confirmMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: confirmMsg.type === "success" ? "#166534" : "#991b1b" }}>
              {confirmMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" style={{ backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowConfirmModal(false)}>No</Button>
          {!confirmMsg?.type && (
            <Button variant="primary" size="sm" style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "none" }} disabled={confirmBusy} onClick={handleConfirmAction}>
              {confirmBusy ? <Spinner animation="border" size="sm" /> : "Yes"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Compute Grade Modal */}
      <Modal show={showGradeModal} onHide={() => setShowGradeModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: "var(--brand-primary-active)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Confirm ?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <p>Are you sure you want to Compute Student Grade?</p>
          <p style={{ color: "var(--status-danger)", fontSize: "0.82rem" }}>*NOTE : The data will be overwritten if already computed*</p>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Type</label>
            <select
              style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border-strong)", width: 250 }}
              value={gradeTypeId}
              onChange={(e) => handleGradeTypeChange(Number(e.target.value))}
            >
              <option value={1}>Absolute</option>
              <option value={2}>Relative</option>
            </select>
          </div>
          {gradeBoundariesLoading ? (
            <div style={{ padding: 10, fontSize: "0.82rem", color: "var(--text-muted)" }}>Fetching data, please wait...</div>
          ) : gradeBoundaries.length > 0 ? (
            <>
              <div style={{ color: "var(--status-success)", fontWeight: 600, marginBottom: 6 }}>Existing grade boundaries:</div>
              <div style={{ maxHeight: 300, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-sunken)" }}>
                      <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>Test Name</th>
                      <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "center" }}>A</th>
                      <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "center" }}>B</th>
                      <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "center" }}>C</th>
                      <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "center" }}>D</th>
                      <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "center" }}>E</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradeBoundaries.map((test, idx) => {
                      const gb: Record<string, string> = {};
                      (test.gradeBoundaryJson || []).forEach((b: any) => { gb[b.grade] = `${b.lowerBound} - ${b.upperBound}`; });
                      return (
                        <tr key={idx} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                          <td style={{ padding: "6px 10px" }}>{test.testName}</td>
                          {["A", "B", "C", "D", "E"].map((g) => (
                            <td key={g} style={{ padding: "6px 10px", textAlign: "center" }}>{gb[g] || ""}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 12, color: "var(--status-danger)" }}>
                <label style={{ cursor: "pointer" }}>
                  <input type="checkbox" checked={eraseGradeBoundaries}
                    onChange={(e) => setEraseGradeBoundaries(e.target.checked)} style={{ marginRight: 6 }} />
                  <strong>Erase already existing Grade boundaries and recompute them again?</strong>
                </label>
              </div>
            </>
          ) : null}
          {gradeMsg && (
            <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: gradeMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: gradeMsg.type === "success" ? "#166534" : "#991b1b" }}>
              {gradeMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" style={{ backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowGradeModal(false)}>No</Button>
          {!gradeMsg?.type && (
            <Button variant="primary" size="sm" style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "none" }} disabled={gradeBusy} onClick={handleComputeGrade}>
              {gradeBusy ? <Spinner animation="border" size="sm" /> : "Yes"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* SGPA / CGPA Modal */}
      <Modal show={showSgpaCgpaModal} onHide={() => setShowSgpaCgpaModal(false)} centered>
        <Modal.Header closeButton style={{ background: "var(--brand-primary-active)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Confirm Operation</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <p>Which operation would you like to perform?</p>
          <p style={{ color: "var(--status-danger)", fontSize: "0.82rem" }}>*NOTE : The data will be overwritten if already computed*</p>
          {!sgpaCgpaMsg?.type && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
              <button
                disabled={sgpaCgpaBusyMode !== null}
                onClick={() => handleComputeSgpaCgpa("sgpa")}
                style={{
                  padding: "12px 20px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: "0.9rem", backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {sgpaCgpaBusyMode === "sgpa" ? <Spinner animation="border" size="sm" /> : null}
                Compute SGPA
              </button>
              <button
                disabled={sgpaCgpaBusyMode !== null}
                onClick={() => handleComputeSgpaCgpa("cgpa")}
                style={{
                  padding: "12px 20px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: "0.9rem", backgroundColor: "var(--brand-accent)", color: "var(--text-on-brand)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {sgpaCgpaBusyMode === "cgpa" ? <Spinner animation="border" size="sm" /> : null}
                Compute CGPA
              </button>
            </div>
          )}
          {sgpaCgpaMsg && (
            <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 8, fontSize: "0.85rem",
              background: sgpaCgpaMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: sgpaCgpaMsg.type === "success" ? "#166534" : "#991b1b" }}>
              {sgpaCgpaMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowSgpaCgpaModal(false)}>
            {sgpaCgpaMsg?.type ? "Close" : "Cancel"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Publish Report Modal */}
      <Modal show={showPublishReportModal} onHide={() => setShowPublishReportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Confirm Operation</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <p>Which operation would you like to perform?</p>
          {publishReportStatus ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: 8, background: publishReportStatus.publishToStaff ? "#dcfce7" : "#fee2e2" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>Staff</div>
                  <div style={{ fontSize: "0.78rem", color: publishReportStatus.publishToStaff ? "#166534" : "#991b1b" }}>
                    {publishReportStatus.publishToStaff ? "Currently Published" : "Not Published"}
                  </div>
                </div>
                <button
                  disabled={publishReportBusy}
                  onClick={() => handlePublishReportToggle(1)}
                  style={{
                    minWidth: 140, padding: "8px 16px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
                    backgroundColor: publishReportStatus.publishToStaff ? "#f59e0b" : "#2563eb",
                    color: "var(--text-on-brand)",
                  }}
                >
                  {publishReportBusy ? "..." : publishReportStatus.publishToStaff ? "Unpublish" : "Publish to Staff"}
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: 8, background: publishReportStatus.publishToStudent ? "#dcfce7" : "#fee2e2" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>Students</div>
                  <div style={{ fontSize: "0.78rem", color: publishReportStatus.publishToStudent ? "#166534" : "#991b1b" }}>
                    {publishReportStatus.publishToStudent ? "Currently Published" : "Not Published"}
                  </div>
                </div>
                <button
                  disabled={publishReportBusy}
                  onClick={() => handlePublishReportToggle(2)}
                  style={{
                    minWidth: 140, padding: "8px 16px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
                    backgroundColor: publishReportStatus.publishToStudent ? "#f59e0b" : "#2563eb",
                    color: "var(--text-on-brand)",
                  }}
                >
                  {publishReportBusy ? "..." : publishReportStatus.publishToStudent ? "Unpublish" : "Publish to Students"}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}><Spinner animation="border" size="sm" /></div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowPublishReportModal(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Exam Dates Modal */}
      <Modal show={showExamDatesModal} onHide={() => setShowExamDatesModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: "var(--brand-primary)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Exam Dates</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          {examDatesLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" /></div>
          ) : (
            <>
              <div style={{ maxHeight: 450, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 6px", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-sunken)" }}>
                      <th style={{ padding: "6px 10px" }}>Section</th>
                      <th style={{ padding: "6px 10px" }}>Start Date</th>
                      <th style={{ padding: "6px 10px" }}>End Date</th>
                      <th style={{ padding: "6px 10px" }}>Type</th>
                      <th style={{ padding: "6px 10px", width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {examDatesRows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: "4px 8px" }}>
                          <select style={{ width: "100%", padding: "4px 8px", borderRadius: 4, border: "1px solid var(--border-strong)", fontSize: "0.82rem" }}
                            value={row.batchId} onChange={(e) => updateExamDateRow(idx, "batchId", Number(e.target.value))}>
                            <option value={0}>---SELECT---</option>
                            {examDatesBatches.map((b) => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          <input type="date" style={{ padding: "4px 8px", border: "1px solid var(--border-strong)", borderRadius: 4, fontSize: "0.82rem" }}
                            value={row.startDate} onChange={(e) => updateExamDateRow(idx, "startDate", e.target.value)} />
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          <input type="date" style={{ padding: "4px 8px", border: "1px solid var(--border-strong)", borderRadius: 4, fontSize: "0.82rem" }}
                            value={row.endDate} onChange={(e) => updateExamDateRow(idx, "endDate", e.target.value)} />
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          <select style={{ width: "100%", padding: "4px 8px", borderRadius: 4, border: "1px solid var(--border-strong)", fontSize: "0.82rem" }}
                            value={row.indexId} onChange={(e) => updateExamDateRow(idx, "indexId", Number(e.target.value))}>
                            {examDatesIndexes.map((ix) => (
                              <option key={ix.id} value={ix.id}>{ix.name}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: "4px 8px", textAlign: "center" }}>
                          {row.mappingId === 0 && (
                            <button onClick={() => removeExamDateRow(idx)}
                              style={{ padding: "3px 10px", borderRadius: 4, border: "none", cursor: "pointer", background: "var(--status-danger)", color: "var(--text-on-brand)", fontSize: "0.78rem" }}>
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ textAlign: "right", marginTop: 8 }}>
                <button onClick={addExamDateRow}
                  style={{ padding: "5px 14px", borderRadius: 4, border: "1px solid var(--border-strong)", background: "var(--surface-card)", cursor: "pointer", fontSize: "0.82rem" }}>
                  Add +
                </button>
              </div>
            </>
          )}
          {examDatesMsg && (
            <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: examDatesMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: examDatesMsg.type === "success" ? "#166534" : "#991b1b" }}>
              {examDatesMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowExamDatesModal(false)}>Close</Button>
          <Button variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }} disabled={examDatesSaving} onClick={handleExamDatesSave}>
            {examDatesSaving ? <Spinner animation="border" size="sm" /> : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Category Config (Settings) Modal */}
      <Modal show={showCatConfigModal} onHide={() => setShowCatConfigModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {catConfigTarget?.termName} :: {catConfigTarget?.categoryName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          {catConfigLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" /></div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <label style={{ fontWeight: 500, whiteSpace: "nowrap" }}>Merge Logic</label>
                <select
                  style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border-strong)", width: 200 }}
                  value={catConfigMergeLogic}
                  onChange={(e) => setCatConfigMergeLogic(e.target.value)}
                >
                  <option value="1">Logic 1</option>
                  <option value="2">Logic 2</option>
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fce7f3", padding: "8px 14px", borderRadius: "6px 6px 0 0", fontWeight: 600 }}>
                <span>Mapping</span>
                <button
                  onClick={addCatConfigMapping}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", fontWeight: 700, color: "#111" }}
                  title="Add row"
                >+</button>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid var(--border-default)", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ background: "var(--surface-page)" }}>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "left", width: "40%" }}>
                      Test Category<span style={{ color: "var(--status-danger)" }}>*</span>
                    </th>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "left", width: "40%" }}>
                      Course Type 2<span style={{ color: "var(--status-danger)" }}>*</span>
                    </th>
                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", textAlign: "left", width: "20%" }}>
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {catConfigMappings.map((m, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                      <td style={{ padding: "6px 8px" }}>
                        <select
                          style={{ width: "100%", padding: "4px 8px", borderRadius: 4, border: "1px solid var(--border-strong)", fontSize: "0.82rem" }}
                          value={m.catId}
                          onChange={(e) => updateCatConfigMapping(idx, "catId", e.target.value)}
                        >
                          <option value="0">---SELECT---</option>
                          {catConfigCategories.map((c) => (
                            <option key={c.id} value={String(c.id)}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <select
                          style={{ width: "100%", padding: "4px 8px", borderRadius: 4, border: "1px solid var(--border-strong)", fontSize: "0.82rem" }}
                          value={m.courseType2Id}
                          onChange={(e) => updateCatConfigMapping(idx, "courseType2Id", e.target.value)}
                        >
                          <option value="0">---SELECT---</option>
                          {catConfigCourseTypes2.map((c) => (
                            <option key={c.id} value={String(c.id)}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <input
                          type="number"
                          style={{ width: "100%", padding: "4px 8px", borderRadius: 4, border: "1px solid var(--border-strong)", fontSize: "0.82rem" }}
                          value={m.value}
                          onChange={(e) => updateCatConfigMapping(idx, "value", e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {catConfigMsg && (
            <div style={{
              marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: catConfigMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: catConfigMsg.type === "success" ? "#166534" : "#991b1b",
            }}>
              {catConfigMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowCatConfigModal(false)}>
            Close
          </Button>
          <Button variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }} disabled={catConfigSaving} onClick={handleCatConfigSave}>
            {catConfigSaving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Merge result modal */}
      <Modal show={!!mergeAllMsg} onHide={() => setMergeAllMsg(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Merge Result</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <div style={{
            padding: "10px 14px", borderRadius: 6,
            background: mergeAllMsg?.type === "success" ? "#dcfce7" : "#fee2e2",
            color: mergeAllMsg?.type === "success" ? "#166534" : "#991b1b",
          }}>
            {mergeAllMsg?.text}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setMergeAllMsg(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Open Entry confirmation modal */}
      <Modal show={showOpenEntryModal} onHide={() => setShowOpenEntryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Open Test Data Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          {openEntryTarget && (
            <h5 style={{ fontSize: "0.95rem", fontWeight: 400 }}>
              Do you want to open up test marks entry for the{" "}
              <strong>{data?.program.name}</strong> / <strong>{data?.year.name}</strong> / <strong>{openEntryTarget.termName}</strong> / <strong>{openEntryTarget.categoryName}</strong>?
            </h5>
          )}
          {openEntryMsg && (
            <div style={{
              marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
              background: openEntryMsg.type === "success" ? "#dcfce7" : "#fee2e2",
              color: openEntryMsg.type === "success" ? "#166534" : "#991b1b",
            }}>
              {openEntryMsg.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }} onClick={() => setShowOpenEntryModal(false)}>
            Cancel
          </Button>
          {!openEntryMsg?.type && (
            <Button variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }} disabled={openEntryBusy} onClick={handleOpenEntry}>
              {openEntryBusy ? <Spinner animation="border" size="sm" /> : "Open Entry"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
